import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BugIcon, LightbulbIcon, QuestionIcon, PaperclipIcon, XIcon, PaperPlaneRightIcon } from "@phosphor-icons/react";

const CATEGORIES = [
  { k: "Bug",        label: "Bug",        icon: BugIcon,      cls: "text-red-400" },
  { k: "Suggestion", label: "Suggestion", icon: LightbulbIcon, cls: "text-amber-400" },
  { k: "Question",   label: "Question",   icon: QuestionIcon,  cls: "text-blue-400" },
];

const MAX_SCREENSHOT_BYTES = 2 * 1024 * 1024; // 2 MB

// Any-user modal for submitting feedback about the app. Sends
// { category, subject, description, screenshot } to POST /api/feedback/.
// Screenshot is optional and stored as a base64 data URL (small images only).
export default function FeedbackDialog({ open, onOpenChange, onSubmitted }) {
  const empty = { category: "Suggestion", subject: "", description: "", screenshot: "" };
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/^image\//.test(file.type)) { toast.error("Only image files allowed"); return; }
    if (file.size > MAX_SCREENSHOT_BYTES) { toast.error("Screenshot too large — keep under 2 MB"); return; }
    const reader = new FileReader();
    reader.onload = () => setForm(f => ({ ...f, screenshot: reader.result || "" }));
    reader.readAsDataURL(file);
  };

  const submit = async () => {
    if (!form.subject.trim()) { toast.error("Subject is required"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/feedback/", form);
      toast.success("Thanks — your feedback was recorded");
      setForm(empty);
      onOpenChange(false);
      onSubmitted && onSubmitted(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to submit feedback");
    }
    setSubmitting(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) setForm(empty); }}>
      <DialogContent
        data-testid="feedback-dialog"
        className="bg-[var(--bg-1)] border-[var(--border)] text-white max-w-xl"
        description="Report a bug or request a feature — attach a screenshot to help us investigate."
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <PaperPlaneRightIcon size={18} weight="bold" className="text-[var(--accent)]" />
            System Feedback
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3.5">
          <div>
            <div className="overline mb-1.5">Category</div>
            <div className="grid grid-cols-3 gap-2">
              {CATEGORIES.map(c => {
                const Icon = c.icon;
                const active = form.category === c.k;
                return (
                  <button
                    key={c.k}
                    type="button"
                    data-testid={`feedback-category-${c.k}`}
                    onClick={() => setForm(f => ({ ...f, category: c.k }))}
                    className={`flex items-center gap-2 px-3 h-10 border rounded-sm text-sm font-semibold transition ${active ? "border-[var(--accent)] bg-[var(--accent)]/10 text-white" : "border-[var(--border)] text-zinc-400 hover:text-white hover:bg-[var(--bg-2)]/60"}`}
                  >
                    <Icon size={14} weight="bold" className={active ? c.cls : ""} />
                    {c.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <div className="overline mb-1.5">Subject *</div>
            <input
              data-testid="feedback-subject"
              value={form.subject}
              onChange={e => setForm(f => ({ ...f, subject: e.target.value.slice(0, 200) }))}
              placeholder="One-line summary of the issue or idea"
              className="w-full h-10 px-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm focus:outline-none focus:border-[var(--accent)]"
              maxLength={200}
            />
            <div className="text-[10px] text-zinc-500 mt-0.5 text-right font-mono">{form.subject.length}/200</div>
          </div>
          <div>
            <div className="overline mb-1.5">Description</div>
            <textarea
              data-testid="feedback-description"
              value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Steps to reproduce, what you expected, what you saw, or your idea in more detail…"
              rows={5}
              className="w-full px-3 py-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm focus:outline-none focus:border-[var(--accent)] resize-y"
              data-nouppercase
            />
          </div>
          <div>
            <div className="overline mb-1.5">Screenshot · optional · max 2 MB</div>
            {form.screenshot ? (
              <div className="relative inline-block">
                <img
                  data-testid="feedback-screenshot-preview"
                  src={form.screenshot}
                  alt="preview"
                  className="max-h-40 border border-[var(--border)] rounded-sm"
                />
                <button
                  type="button"
                  data-testid="feedback-screenshot-remove"
                  onClick={() => setForm(f => ({ ...f, screenshot: "" }))}
                  className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center hover:bg-red-600"
                  title="Remove screenshot"
                >
                  <XIcon size={11} weight="bold" />
                </button>
              </div>
            ) : (
              <label className="inline-flex items-center gap-2 h-10 px-3 border border-dashed border-[var(--border)] rounded-sm text-sm text-zinc-400 hover:text-white hover:border-[var(--accent)] cursor-pointer" data-testid="feedback-screenshot-label">
                <PaperclipIcon size={14} weight="bold" />
                <span>Attach a screenshot</span>
                <input type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="feedback-screenshot-input" />
              </label>
            )}
          </div>
        </div>
        <DialogFooter>
          <button
            onClick={() => onOpenChange(false)}
            data-testid="feedback-cancel"
            className="px-4 py-2 rounded-sm border border-[var(--border)] text-sm text-zinc-400 hover:text-white"
          >Cancel</button>
          <button
            onClick={submit}
            disabled={submitting}
            data-testid="feedback-submit"
            className="px-4 py-2 rounded-sm bg-[var(--accent)] hover:bg-blue-600 text-sm font-semibold text-white disabled:opacity-50 flex items-center gap-2"
          >
            <PaperPlaneRightIcon size={13} weight="bold" />
            {submitting ? "Submitting…" : "Submit Feedback"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
